<template>
  <v-dialog v-model="visible" persistent max-width="638">
    <v-card v-if="visible" class="v-dialog--custom">
      <v-card-title class="headline pa-4">
        <span class="v-dialog--custom__title my-2">{{ $t(`${namespace}.updateEmployeeSupervisorTitle`) }}</span>
        <v-btn class="v-dialog--custom__close mr-2" fab flat color="gray-6" :disabled="isLoading" @click="close">
          <v-icon dark>close</v-icon>
        </v-btn>
      </v-card-title>

      <form class="form--custom" novalidate @submit.prevent="batchUpdate">
        <v-card-text class="px-4 pt-0">
          <p class="pb-2">{{ $t(`${namespace}.updateEmployeeSupervisorMsg`) }}</p>
          <p><strong>{{ $t(`${namespace}.updateEmployeeSupervisorStep1`) }}</strong></p>

          <v-radio-group
            v-model="radioGroup"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t('Common.employees'))"
            :data-vv-name="$t('Common.employees')"
            required
          >
            <v-radio :label="$t(`${namespace}.allEmployees`)" value="allEmployees"></v-radio>
            <v-radio :label="$t(`${namespace}.selectEmployeesByName`)" value="perName"></v-radio>
            <v-radio :label="$t(`${namespace}.selectEmployeesById`)" value="perId"></v-radio>
          </v-radio-group>

          <AutoComplete
            v-if="radioGroup === 'perName'"
            kind="employee"
            :data="employeeIds"
            :companyId="company.id"
            :placeholder="$t('Components.ApplyToAllDialog.searchEmployees')"
            :isValid="radioGroup === 'perName' && isEmployeeValid"
            :required="radioGroup === 'perName'"
            multiple
            @input="updateEmployee"
          />

          <v-text-field
            v-if="radioGroup === 'perId'"
            v-model="employeesIdsField"
            flat
            outline
            solo
            color="accent"
            clearable
            :name="$t(`${namespace}.fieldIdsName`)"
            :placeholder="$t(`${namespace}.fieldIdsPlaceholder`)"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t(`${namespace}.fieldIdsName`))"
            :data-vv-name="$t(`${namespace}.fieldIdsName`)"
            required
            @blur="stringToArray"
          />

          <p><strong>{{ $t(`${namespace}.updateEmployeeSupervisorStep2`) }}</strong></p>

          <AutoComplete
            kind="employee"
            :companyId="company.id"
            :placeholder="$t('Components.EmployeeForm.searchSupervisor')"
            :isValid="isSupervisorValid"
            required
            @input="updateSupervisor"
          />
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />

          <a
            v-if="!isLoading"
            href="#"
            class="text--normal accent--text mr-4"
            color="accent"
            flat
            round
            @click.prevent="close"
          >
            {{ $t('Common.cancel') }}
          </a>

          <v-btn
            type="submit"
            :disabled="isLoading"
            :loading="isLoading"
            round
            depressed
            color="accent"
          >
            {{ $t(`${namespace}.updateSupervisor`) }}
          </v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isArray, isObject } from 'utils/functions'
import AutoComplete from 'components/AutoComplete'

export default {
  name: 'SupervisorBulkUpdate',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  components: {
    AutoComplete
  },
  computed: {
    ...mapState({
      company: state => state.company.selectedCompany
    })
  },
  data () {
    return {
      namespace: 'Components.ApplyToAllDialog',
      radioGroup: null,
      isLoading: false,

      // custom validation
      isEmployeeValid: true,
      isSupervisorValid: true,

      // fields
      supervisorId: null,
      employeeIds: [],
      employeesIdsField: null
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    isValidObject (val) {
      return isObject(val)
    },

    resetEmployeeList () {
      this.employeeIds = []
      this.employeesIdsField = null
    },

    resetAllFields () {
      this.resetEmployeeList()
      this.radioGroup = null
      this.supervisorId = null
      this.isEmployeeValid = true
      this.isSupervisorValid = true
    },

    stringToArray () {
      if (!this.employeesIdsField) return
      const arr = this.employeesIdsField.replace(/[[A-zÀ-ú]-\s._]/g, '').split(',')
      this.employeeIds = arr.map(item => Number(item))
    },

    close () {
      this.$emit('close')
    },

    updateEmployee (val) {
      if (!this.isValidArray(val)) {
        this.employeeIds = []
        this.radioGroup !== 'allEmployees' && (this.isEmployeeValid = false)
        return
      }

      this.employeeIds = val
      this.radioGroup !== 'allEmployees' && (this.isEmployeeValid = true)
    },

    updateSupervisor (val) {
      if (!this.isValidObject(val)) {
        this.supervisorId = null
        this.isSupervisorValid = false
        return
      }

      this.supervisorId = val.id
      this.isSupervisorValid = true
    },

    async batchUpdate () {
      if (this.radioGroup !== 'allEmployees' && !this.employeeIds.length) {
        this.isEmployeeValid = false
      }

      if (!this.supervisorId) {
        this.isSupervisorValid = false
      }

      try {
        const isValid = await this.$validator.validateAll()

        if (
          (this.radioGroup !== 'allEmployees' && !this.employeeIds.length) ||
          !this.supervisorId
        ) {
          return
        }

        if (isValid) {
          this.isLoading = true

          // WORKAROUND
          let ids = null

          if (this.radioGroup === 'perName') {
            const employeeIds = this.employeeIds.map(item => item.id)
            ids = this.employeeIds.length ? employeeIds : null
          } else {
            ids = this.employeeIds
          }

          await this.$apiService.batchUpdate(
            this.company.id,
            { budget: null },
            { budgetDate: null },
            { budgetPeriod: 'monthly' },
            { credit: null },
            { useBudget: null },
            { useRideCredit: null },
            { categoryIds: null },
            { supervisorId: this.supervisorId },
            { thirdPartyWebCallPermission: null },
            { webCallPermission: null },
            { ids }
          )

          this.successAlert(this.$t(`${this.namespace}.updateEmployeeSupervisorSuccess`))
          this.resetAllFields()
          this.close()
          this.isLoading = false
        }
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.error`))
        this.isLoading = false
      }
    }
  },
  watch: {
    radioGroup () {
      this.resetEmployeeList()
    },

    employeesIdsField (val) {
      if (!val) {
        this.resetEmployeeList()
      }
    },

    visible (val) {
      // fix validation error when close dialog
      this.$validator._paused = !val

      this.resetAllFields()

      this.radioGroup = null
      this.isLoading = false

      // fields
      this.supervisorId = null
      this.employeeIds = []
      this.employeesIdsField = null

      val && this.$ga.page('/dialog/colaboradores/atualizar-supervisor-em-massa')
    }
  }
}
</script>
